import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation, useIsMounted } from '@ps/web-shared-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import InfoBox from '@swf/components/InfoBox/InfoBox';
import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import type { Limits } from '../../../../../../types/responsibleGamingLimits';
import { getDepositLimitAmountRestrictions } from '../../../../../../utils/responsibleGamingLimits';
import Container from '../../../../../Container/Container';
import ResponsibleGamingLimits from '../../../../../ResponsibleGamingLimits/ResponsibleGamingLimits';

import {
  TXTCLI_Deposit_Limits,
  TXTWCL_Deposit_Limit_5,
  TXTWCL_Deposit_limit_is_a_cap_on_how__ELL,
  TXTWCL_Deposit_limits_control_how_muc_ELL,
  TXTWCL_Once_setX_this_amount_will_alw_ELL,
  TXTWCL_Responsible_Gaming_Limits,
  TXTWCL_Your_deposit_limit_will_be_con_ELL
} from './RMFDepositLimitFields.messages.json';

interface Props {
  handleBackClick?: () => void;
}

const subtitleText = (weeklyOnly: boolean) =>
  weeklyOnly
    ? TXTWCL_Deposit_limit_is_a_cap_on_how__ELL
    : TXTWCL_Deposit_limits_control_how_muc_ELL;

const RMFDepositLimitFields: React.FC<Props> = ({ handleBackClick }) => {
  const request = useRequest();
  const siteId = useSelector(select.cms.getSiteId);
  const isGreece = siteId === Sites.GR;
  const isSpain = siteId === Sites.SPA;
  const isKYCRequired = useSelector(select.account.isKYCRequired);
  const isAgeVerified = useSelector(select.account.isAgeVerified);
  const isAuthVerified = useSelector(select.account.isAuthVerified);
  const defaultSystemCurrency = useSelector(select.account.getLimitCurrency);
  const weeklyOnly = useFeature(FeatureFlag.SwcRmfShowOnlyWeeklyLimit);
  const showConversionInfo = useFeature(FeatureFlag.SwcDepositLimitCurrencyConversionInfo);
  const limitsDisabled = isSpain && (isKYCRequired || !isAgeVerified || !isAuthVerified);

  const {
    data: { mitIdResultContext, depositLimits }
  } = useWizardContext();

  const isMounted = useIsMounted();
  const [fetchedLimits, setFetchedLimits] = useState<Limits>({} as Limits);

  const fetchUserLimits = useCallback(() => {
    request({ service: 'responsiblegaming.userlimit' }).subscribe(({ body }) => {
      const depositLimitAmountRestrictions = getDepositLimitAmountRestrictions(body);
      if (
        isSpain &&
        depositLimitAmountRestrictions.daily.limit === -1 &&
        depositLimitAmountRestrictions.weekly.limit === -1 &&
        depositLimitAmountRestrictions.monthly.limit === -1
      ) {
        depositLimitAmountRestrictions.daily.limit = 600;
        depositLimitAmountRestrictions.weekly.limit = 1500;
        depositLimitAmountRestrictions.monthly.limit = 3000;
      }

      isMounted() && setFetchedLimits(depositLimitAmountRestrictions);
    });
  }, [isMounted, isSpain, request]);

  useEffect(() => {
    if (!isGreece && isMounted()) fetchUserLimits();
  }, [fetchUserLimits, isGreece, isMounted]);

  const greekLimits = useMemo(
    () => (isGreece && depositLimits ? getDepositLimitAmountRestrictions(depositLimits) : null),
    [isGreece, depositLimits]
  );

  const limits = greekLimits || fetchedLimits;

  return (
    <Container
      page={RMFDepositLimitFields}
      headerProps={{
        title: <Translation {...TXTWCL_Responsible_Gaming_Limits} />,
        buttons: {
          back: siteId !== Sites.DEN || !mitIdResultContext,
          onBackClick: handleBackClick
        }
      }}
      pageTopTitle={
        <Translation {...(weeklyOnly ? TXTWCL_Deposit_Limit_5 : TXTCLI_Deposit_Limits)} />
      }
      pageTopSubtitle={<Translation {...subtitleText(weeklyOnly)} />}
    >
      {showConversionInfo && (
        <InfoBox
          title={TXTWCL_Your_deposit_limit_will_be_con_ELL}
          values={{ 0: defaultSystemCurrency }}
          collapsible
        >
          <Translation {...TXTWCL_Once_setX_this_amount_will_alw_ELL} />
        </InfoBox>
      )}
      <ResponsibleGamingLimits
        limits={limits}
        limitsDisabled={limitsDisabled}
        fieldName="rmf.deposit"
      />
    </Container>
  );
};

RMFDepositLimitFields.displayName = 'RMFDepositLimitFields';

export default RMFDepositLimitFields;
